<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Check, ChevronLeft, Flame } from 'lucide-vue-next'
import { apiGetMembershipPlans } from '@/apis/membership'
import type { IMembershipPlan } from '@/types/apis/membership'
import { PaymentProvider } from '@/enums/membership'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import { getLocale } from '@/i18n'
import PaymentPopup from '@/pages/plan/components/payment-popup.vue'
import type { IPurchaseSummary } from './types'

const router = useRouter()
const { t } = useI18n()
const appStore = useAppStore()
const userStore = useUserStore()

const isChineseLocale = computed(() => getLocale() === 'zh-CN')
const plans = ref<IMembershipPlan[]>([])
const selectedPlanId = ref<number | null>(null)
const loadingPlans = ref(false)
const paymentPopupVisible = ref(false)

const sortedPlans = computed(() => [...plans.value].sort((a, b) => b.SortOrder - a.SortOrder))
const selectedPlan = computed(
  () => sortedPlans.value.find(plan => plan.Id === selectedPlanId.value) ?? sortedPlans.value[0] ?? null,
)
const paymentPopupPlanId = computed(() => selectedPlan.value?.Id ?? 0)
const selectedPlanSummary = computed<IPurchaseSummary | null>(() => {
  if (!selectedPlan.value) return null

  return {
    title: selectedPlan.value.PlanName,
    description: selectedPlan.value.Description,
    priceCny: selectedPlan.value.PriceCNY,
    priceUsd: selectedPlan.value.PriceUSD,
    originalPriceCny: selectedPlan.value.OriginalPriceCNY,
    originalPriceUsd: selectedPlan.value.OriginalPriceUSD,
    validityDays: selectedPlan.value.DurationDays,
    points: selectedPlan.value.GiftPoints,
  }
})

// 当前用户已购套餐的 SortOrder（未激活时为 Infinity 表示全部可买）
const currentPlanSortOrder = computed(() => {
  const profile = userStore.profileInfo
  if (!profile?.IsActive) return Infinity
  return plans.value.find(p => p.Id === profile.PlanId)?.SortOrder ?? Infinity
})

// 套餐是否允许购买：未激活则全部可买；已激活则只能买 SortOrder 严格小于当前套餐的
const isPlanPurchasable = (plan: IMembershipPlan) => {
  return plan.SortOrder < currentPlanSortOrder.value
}

const formatPrice = (plan: IMembershipPlan) => {
  const symbol = isChineseLocale.value ? '¥' : '$'
  const price = isChineseLocale.value ? plan.PriceCNY : plan.PriceUSD
  const originalPrice = isChineseLocale.value ? plan.OriginalPriceCNY : plan.OriginalPriceUSD
  return {
    current: `${symbol}${price.toFixed(2)}`,
    original: originalPrice > price ? `${symbol}${originalPrice.toFixed(2)}` : '',
  }
}

const openPaymentPopup = (plan: IMembershipPlan) => {
  if (!isPlanPurchasable(plan)) return
  selectedPlanId.value = plan.Id
  paymentPopupVisible.value = true
}

const goToPointsPage = () => {
  router.push('/plan/points')
}

interface IPaymentResultPayload {
  orderNo: string
  paymentProvider: PaymentProvider
  channelOrderId: string
  planId: number
}

const handlePaid = (payload: IPaymentResultPayload) => {
  router.replace({
    path: '/plan/result',
    query: {
      orderNo: payload.orderNo,
      paymentProvider: String(payload.paymentProvider),
      channelOrderId: payload.channelOrderId,
      planId: String(payload.planId),
    },
  })
}

const initPlans = async () => {
  loadingPlans.value = true
  try {
    const res = await apiGetMembershipPlans()
    plans.value = res.Body.Plans ?? []
  } finally {
    loadingPlans.value = false
  }
}

watch(
  sortedPlans,
  nextPlans => {
    if (!selectedPlanId.value && nextPlans.length > 0) {
      selectedPlanId.value = nextPlans[0]?.Id ?? null
    }
  },
  { immediate: true },
)

onMounted(async () => {
  await Promise.all([initPlans(), userStore.refreshProfileInfo()])
})
</script>

<template>
  <div class="h-screen bg-gray-950 text-white overflow-y-auto">
    <div class="mx-auto max-w-6xl px-4 pb-28 pt-5 sm:px-6 lg:px-8 lg:pb-10">
      <div class="grid gap-6">
        <button class="inline-flex items-center gap-2 text-sm text-white/80" @click="router.back()">
          <ChevronLeft :size="24" />
          {{ t('plan.page.back') }}
        </button>
        <section
          class="overflow-hidden rounded-[28px] border border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(147,51,234,0.28),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] px-4 py-8 shadow-[0_24px_80px_rgba(15,23,42,0.4)] sm:p-8"
        >
          <div class="mb-6 flex flex-col gap-4 rounded-3xl border border-white/10 bg-white/5 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div class="text-sm text-white/50">{{ t('component.planPage.pointsBalance') }}</div>
              <div class="mt-2 text-2xl font-semibold text-white flex items-center gap-2">
                <Flame :size="22"  fill="currentcolor"/>
                {{ userStore.profileInfo?.AvailablePoints ?? 0 }}
              </div>
              <p class="mt-2 text-sm leading-6 text-white/60">{{ t('component.planPage.pointsEntryDescription') }}</p>
            </div>
            <button
              class="inline-flex h-11 items-center justify-center rounded-2xl border border-white/15 bg-white/8 px-5 text-sm text-white/80 transition hover:bg-white/12 hover:text-white"
              @click="goToPointsPage"
            >
              {{ t('component.planPage.buyPoints') }}
            </button>
          </div>

          <!-- 移动端：横向滚动选择套餐 + 套餐详情卡片 -->
          <div v-if="appStore.isMobile">
            <div class="no-scrollbar -mx-2 mb-5 overflow-x-auto px-2">
              <div class="grid grid-cols-3 gap-2">
                <button
                  v-for="plan in sortedPlans"
                  :key="plan.Id"
                  class="rounded-2xl border px-4 py-2 text-sm font-medium transition-all duration-200"
                  :class="
                    selectedPlanId === plan.Id
                      ? 'border-purple-600/60 bg-purple-600/10 text-white'
                      : 'border-white/10 bg-white/5 text-white/60'
                  "
                  @click="selectedPlanId = plan.Id"
                >
                  {{ plan.PlanName?.replace('VIP', '') }}
                </button>
              </div>
            </div>

            <div
              class="overflow-hidden rounded-[30px] border border-purple-600/60 bg-purple-600/5 px-4 py-6"
            >
              <div class="flex items-start justify-between gap-4">
                <div>
                  <div class="text-xl font-semibold">
                    {{ selectedPlan?.PlanName || t('component.planPage.pleasePleaseSelectPlan') }}
                  </div>
                  <div class="mt-2 text-sm text-white/60">
                    {{ t('component.planPage.validityDays', { days: selectedPlan?.DurationDays ?? 0 }) }}
                  </div>
                </div>
                <div class="rounded-full bg-white/10 px-3 py-1 text-xs text-white/75">
                  {{ t('component.planPage.giftPoints', { num: selectedPlan?.GiftPoints ?? 0 }) }}
                </div>
              </div>

              <div class="mt-6 flex items-end gap-2">
                <span class="text-4xl font-semibold text-white">
                  {{ selectedPlan ? formatPrice(selectedPlan).current : '--' }}
                </span>
                <span
                  v-if="selectedPlan && formatPrice(selectedPlan).original"
                  class="pb-1 text-sm text-white/35 line-through"
                >
                  {{ formatPrice(selectedPlan).original }}
                </span>
              </div>

              <p class="mt-4 min-h-12 text-sm leading-6 text-white/70">
                {{ selectedPlan?.Description || t('component.planPage.defaultDescription') }}
              </p>

              <div class="mt-5 flex flex-col flex-wrap gap-2">
                <div
                  v-for="feature in getLocale() === 'zh-CN' ? selectedPlan?.Features : (selectedPlan?.FeaturesEn ?? [])"
                  :key="feature"
                  class="text-md text-white/75 flex gap-2 items-center"
                >
                  <Check :size="16" />
                  <div>{{ feature }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- PC 端：套餐卡片网格 -->
           <template v-else>
            <div class="max-w-2xl  mb-8">
              <h1 class="text-3xl font-semibold tracking-wide">
                {{ t('store.messages.membershipBenefit') }}
              </h1>
              <p class="mt-3 max-w-xl text-sm leading-6 text-white/65 sm:text-base">
                {{ t('plan.page.packageDescription') }}
              </p>
            </div>

            <div  class="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3 py-8">
              <div
                v-for="plan in sortedPlans"
                :key="plan.Id"
                class="group cursor-pointer relative overflow-hidden rounded-3xl border p-5 text-left transition-all duration-200"
                :class="
                  selectedPlanId === plan.Id
                    ? 'border-purple-600/60 bg-purple-600/5 shadow-[0_12px_40px_rgba(245,158,11,0.14)]'
                    : 'border-white/10 bg-white/3 hover:border-white/20 hover:bg-white/5'
                "
                @click="selectedPlanId = plan.Id"
              >
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <div class="text-lg font-semibold">{{ plan.PlanName }}</div>
                    <div class="mt-1 text-xs text-white/50">
                      {{ t('component.planPage.validityDays', { days: plan.DurationDays }) }}
                    </div>
                  </div>
                  <div class="rounded-full bg-white/8 px-3 py-1 text-xs text-white/70">
                    {{ t('component.planPage.giftPoints', { num: plan.GiftPoints }) }}
                  </div>
                </div>

                <div class="mt-6 flex items-end gap-2">
                  <span class="text-3xl font-semibold text-white">{{ formatPrice(plan).current }}</span>
                  <span v-if="formatPrice(plan).original" class="pb-1 text-sm text-white/35 line-through">
                    {{ formatPrice(plan).original }}
                  </span>
                </div>

                <p class="mt-4 line-clamp-2 min-h-10 text-sm text-white/60">
                  {{ plan.Description || t('component.planPage.defaultDescription') }}
                </p>

                <div class="mt-4 flex flex-col flex-wrap gap-2">
                  <div
                    v-for="feature in getLocale() === 'zh-CN' ? plan.Features : plan.FeaturesEn"
                    :key="feature"
                    class="text-lg text-white/70 flex gap-2 items-center"
                  >
                    <Check :size="16" /> {{ feature }}
                  </div>
                </div>

                <button
                  :class="[selectedPlanId === plan.Id ? 'visible' : 'invisible']"
                  class="mt-20 flex h-11 w-full items-center justify-center rounded-2xl bg-purple-600 text-sm font-semibold text-slate-900 transition disabled:cursor-not-allowed! disabled:opacity-60"
                  :disabled="loadingPlans || !isPlanPurchasable(plan)"
                  @click.stop="openPaymentPopup(plan)"
                >
                  {{ t('component.planPage.buyNow') }}
                </button>
              </div>
            </div>
           </template>


          <div v-if="!loadingPlans && sortedPlans.length === 0" class="py-16 text-center text-white/45">
            {{ t('component.planPage.noPlans') }}
          </div>
        </section>
      </div>
    </div>

    <!-- 移动端底部购买按钮 -->
    <div
      v-if="appStore.isMobile"
      class="fixed inset-x-0 bottom-0 z-20 px-4 pb-[calc(env(safe-area-inset-bottom,0px)+16px)] pt-4 backdrop-blur"
    >
      <button
        class="flex h-12 w-full items-center justify-center rounded-2xl bg-purple-600 text-base font-semibold text-slate-900 transition hover:bg-purple-600 disabled:cursor-not-allowed disabled:opacity-20"
        :disabled="loadingPlans || !selectedPlan || (!!selectedPlan && !isPlanPurchasable(selectedPlan))"
        @click="selectedPlan && openPaymentPopup(selectedPlan)"
      >
        {{ t('component.planPage.buyNow') }}
      </button>
    </div>

    <!-- 支付弹窗（解耦为独立组件） -->
    <PaymentPopup
      v-model:visible="paymentPopupVisible"
      business-type="membership"
      :plan-id="paymentPopupPlanId"
      :purchase-summary="selectedPlanSummary"
      @paid="handlePaid"
    />
  </div>
</template>
